import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, CheckCircle2, ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "ssp_onboarding_billing_banner_dismissed_until";

/**
 * Slim banner that nudges new dealers to settle their £2,500 onboarding fee.
 * Auto-hides when:
 *   - Workshop is already paid (server-side `onboarding_paid_at`)
 *   - User has just dismissed it (7-day localStorage gate)
 *   - Demo / platform-owner / RRG-demo accounts (these never pay)
 */
export default function OnboardingBillingBanner(_props) {
  // Steve — hidden pre-Arran-meeting (Feb 2026). Restore by rendering
  // <OnboardingBillingBannerImpl {...props} /> below instead of returning null,
  // once pricing is finalised. Nothing else has changed.
  return null;
}

function OnboardingBillingBannerImpl({ user }) {
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Suppress on accounts that don't represent a real-paying dealer
    if (user.platform_owner || user.is_demo_user) return;
    // Honour the 7-day dismiss
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until > Date.now()) { setDismissed(true); return; }
    api.get("/billing/onboarding-state")
      .then((r) => setState(r.data))
      .catch(() => {});
  }, [user]);

  if (!state || state.paid || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setDismissed(true);
  };

  return (
    <div className="mb-4 rounded-lg border border-primary/40 bg-primary/[0.08] p-4 flex items-start gap-3" data-testid="onboarding-billing-banner">
      <div className="w-10 h-10 rounded-full bg-primary/15 grid place-items-center shrink-0">
        <CreditCard className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 border border-primary/30 px-1.5 py-0.5 rounded">
            Trial workshop
          </span>
          <span className="text-xs text-muted-foreground">{state.workshop_name}</span>
        </div>
        <div className="mt-1 font-semibold">Activate your workshop with the £2,500 onboarding fee</div>
        <p className="text-xs text-muted-foreground mt-1">
          Unlocks branded SMS, paid add-ons, the 3% commercial model and full team management. Pay securely via Stripe — VAT receipt instant.
        </p>
        <div className="flex items-center gap-3 mt-2.5">
          <Link to="/billing/onboarding" className="btn-primary text-xs px-3 py-1.5 h-auto" data-testid="onboarding-banner-cta">
            Pay £2,500 <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <button onClick={dismiss} className="text-xs text-muted-foreground hover:text-foreground" data-testid="onboarding-banner-dismiss">
            Remind me later
          </button>
        </div>
      </div>
      <button onClick={dismiss} className="rounded-full w-8 h-8 grid place-items-center hover:bg-muted text-muted-foreground" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/** Smaller "Active · onboarding paid" pill for the header — call when needed. */
export function OnboardingPaidPill() {
  const [paid, setPaid] = useState(false);
  useEffect(() => {
    api.get("/billing/onboarding-state").then((r) => setPaid(!!r.data?.paid)).catch(() => {});
  }, []);
  if (!paid) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-success bg-success/10 border border-success/30 px-1.5 py-0.5 rounded" data-testid="onboarding-paid-pill">
      <CheckCircle2 className="w-3 h-3" /> Active
    </span>
  );
}
